import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { userManager } from "../auth/realms";
import { accessToken } from "../auth/token";
import { onEmbedToken } from "../embed/session";
import { apiBase } from "../api/client";
import { patchAlarm, patchAutomation, patchDeviceState, patchLatest } from "./patch";
import { LiveSocket, type LiveStatus, type Target } from "./socket";

type LiveState = { socket: LiveSocket | undefined; status: LiveStatus };
const LiveContext = createContext<LiveState>({ socket: undefined, status: "closed" });
/** Devices the server refused or stopped following (WEB-F-05): their screens poll. */
const NotLiveContext = createContext<ReadonlySet<string>>(new Set());

/** wss://<the API's origin>/v1/live; the API shares the app's origin unless config.json names another (P-106). */
export function liveUrl(apiBase: string, origin: string): string {
  return `${(apiBase || origin).replace(/^http/, "ws")}/v1/live`;
}

/** One live socket for the signed-in customer in one organisation; remount it (key) when the organisation changes. */
export function LiveProvider({ orgId, children }: { orgId: string | undefined; children: ReactNode }) {
  const qc = useQueryClient();
  const [state, setState] = useState<LiveState>({ socket: undefined, status: "closed" });
  const [notLive, setNotLive] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (!orgId) return;
    const manager = userManager("customers");
    const socket = new LiveSocket({
      url: liveUrl(apiBase("customers", orgId), window.location.origin),
      orgId,
      token: () => accessToken("customers"),
      onLatest: (m) => patchLatest(qc, orgId, m),
      onDeviceState: (m) => patchDeviceState(qc, orgId, m),
      onAlarm: (m) => patchAlarm(qc, orgId, m),
      onAutomation: (m) => patchAutomation(qc, orgId, m),
      onStatus: (status) => setState({ socket, status }),
      onFollowing: (target, following) => {
        if (!("device" in target)) return;
        setNotLive((old) => {
          if (following !== old.has(target.device)) return old;
          const next = new Set(old);
          if (following) next.delete(target.device);
          else next.add(target.device);
          return next;
        });
      },
      // A sign out everywhere: forget the tokens, and the shell sends the user to sign in.
      onSessionEnded: () => void manager.removeUser(),
    });
    const onLoaded = (u: { access_token: string }) => socket.refreshed(u.access_token);
    manager.events.addUserLoaded(onLoaded);
    // Inside a phone app's web-view widget, the app's renewed token (mobile-dashboards-2 MD2-03).
    const offEmbed = onEmbedToken((token) => socket.refreshed(token));
    socket.start();
    return () => {
      offEmbed();
      manager.events.removeUserLoaded(onLoaded);
      socket.stop();
    };
  }, [orgId, qc]);

  return (
    <LiveContext.Provider value={state}>
      <NotLiveContext.Provider value={notLive}>{children}</NotLiveContext.Provider>
    </LiveContext.Provider>
  );
}

/**
 * Whether the socket is signed in and follows every one of `devices`; until it does, screens keep polling. A device
 * the server refused or ended (WEB-F-05) keeps its screen polling.
 */
export function useLiveReady(devices: readonly string[] = []): boolean {
  const ready = useContext(LiveContext).status === "ready";
  const notLive = useContext(NotLiveContext);
  return ready && !devices.some((d) => notLive.has(d));
}

/** Follows the devices while the component is on screen (the open page, visible list rows, the map's devices). */
export function useLiveDevices(ids: readonly string[]): void {
  const { socket } = useContext(LiveContext);
  const key = [...ids].sort().join(",");
  useEffect(() => {
    if (!socket || !key) return;
    const stops = key.split(",").map((device) => socket.subscribe({ device } satisfies Target));
    return () => stops.forEach((s) => s());
  }, [socket, key]);
}

/** Follows the organisation's alarms while the component is on screen (live 0.3.0, WEB-ALM-05). */
export function useLiveAlarms(): void {
  const { socket } = useContext(LiveContext);
  useEffect(() => {
    if (!socket) return;
    return socket.subscribe({ alarms: true });
  }, [socket]);
}

/** Follows the organisation's server automations while the component is on screen (live 0.4.0, WAU-11). */
export function useLiveAutomations(enabled = true): void {
  const { socket } = useContext(LiveContext);
  useEffect(() => {
    if (!socket || !enabled) return;
    return socket.subscribe({ automations: true });
  }, [socket, enabled]);
}
